'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queries/queryKeys';
import {
  deleteDiscoveredWorktree,
  fetchDiscoveredWorktrees,
  fetchWorktreeList,
  type DiscoveredWorktreeListItem,
  updateDiscoveredWorktreeLabel,
  updateWorktreeAlias,
} from '../lib/api/daemon';

export function useWorktreeList(token: string | null, projectId: string, includeRemoved: boolean) {
  return useQuery({
    queryKey: queryKeys.daemonTriggers.worktreeList(projectId, includeRemoved),
    queryFn: () => fetchWorktreeList(token ?? '', projectId, includeRemoved),
    enabled: Boolean(token) && projectId.length > 0,
    staleTime: 30_000,
  });
}

export function useUpdateWorktreeAlias(token: string | null, projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ worktreeId, alias }: { worktreeId: string; alias: string }) =>
      updateWorktreeAlias(token ?? '', worktreeId, projectId, alias),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['daemonTriggers', 'worktreeList'] });
      void queryClient.invalidateQueries({ queryKey: ['daemonTriggers', 'activeWorktrees'] });
    },
  });
}

export function useDiscoveredWorktrees(token: string | null, projectId: string) {
  return useQuery({
    queryKey: queryKeys.daemonTriggers.discoveredWorktrees(projectId),
    queryFn: () => fetchDiscoveredWorktrees(token ?? '', projectId),
    enabled: Boolean(token) && projectId.length > 0,
    staleTime: 30_000,
  });
}

export function useUpdateDiscoveredWorktreeLabel(token: string | null, projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) => updateDiscoveredWorktreeLabel(token ?? '', id, label),
    onSuccess: (updatedWorktree) => {
      const queryKey = queryKeys.daemonTriggers.discoveredWorktrees(projectId);
      queryClient.setQueryData<DiscoveredWorktreeListItem[]>(queryKey, (worktrees) =>
        worktrees?.map((worktree) =>
          worktree.id === updatedWorktree.id ? { ...worktree, userLabel: updatedWorktree.userLabel } : worktree,
        ),
      );
      void queryClient.invalidateQueries({
        queryKey,
      });
    },
  });
}

// MISSING 발견 worktree의 레지스트리 데이터만 삭제한다(로컬 파일/브랜치 보존).
export function useDeleteDiscoveredWorktree(token: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDiscoveredWorktree(token ?? '', id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.daemonTriggers.discoveredWorktreesAll });
    },
  });
}
